import intlTelInput from 'intl-tel-input';

export class InputPhoneController {
    constructor(root, input, params) {
        this._root = root;
        this._input = input;

        this._params = {
            preferredCountries: params.preferredCountries ? params.preferredCountries : ['fr', 'gb', 'be', 'ch', 'lu', 'de', 'es', 'it'],
            defaultCountry: params.defaultCountry ? params.defaultCountry : 'FR',
        };

        this._checkInputValue = this._checkInputValue.bind(this);
        this._setActiveState = this._setActiveState.bind(this);
        this._setFocusState = this._setFocusState.bind(this);
        this._removeActiveState = this._removeActiveState.bind(this);
    }

    initialize() {
        // Init the plugin with our fake input
        this._fakeInput = this._createFakeInput();

        // Otherwise utils script is not loading... 🤷
        // intl-tel-input triggers utils script loading (through a promise) on window.onload event.
        // It means that going back and forth with Vue router will not re-load the utils script.
        if (window.utilsScriptLoaded !== 'fulfilled') {
            delete window.intlTelInputUtils;
        }

        this._iti = intlTelInput(this._fakeInput, {
            preferredCountries: this._params.preferredCountries,
            utilsScript: window.intlTelInputUtilsURL,
            customPlaceholder: (selectedCountryPlaceholder) => {
                if (this._params.defaultCountry === 'GB') {
                    // default placeholder for GB is 07400123456
                    return '7400123456';
                }
                return selectedCountryPlaceholder;
            },
        });

        if (window.utilsScriptLoaded === 'fulfilled') {
            this.setDefaultCountry();
        } else {
            this._iti.promise.then(() => {
                window.utilsScriptLoaded = 'fulfilled';
                this.setDefaultCountry();
            });
        }

        this._addListeners();
    }

    destroy() {
        this._removeListeners();
        this._iti.destroy();
        this._iti = null;
        this._input = null;
    }

    setDefaultCountry() {
        if (!this._input.value) {
            // Only set default country when phone number is empty
            this._iti.setCountry(this._params.defaultCountry);
        }
        this._checkInputValueAfterInitialisation();
    }

    isValid() {
        if (this._input.value) {
            return this._iti.isValidNumber();
        }
        return true;
    }

    _createFakeInput() {
        // Here, we create a kind of clone for our original form field.
        // The aim is to show to the user a formatted/readable phone number
        const fakeInput = document.createElement('input');

        fakeInput.setAttribute('type', 'text');
        fakeInput.setAttribute('data-fake-input', '');
        fakeInput.setAttribute('class', 'm-input_phone__fake');
        fakeInput.setAttribute('value', this._input.value);
        if (this._params.placeholder) {
            fakeInput.setAttribute('placeholder', this._params.placeholder);
        }
        this._root.insertBefore(fakeInput, this._input);

        return fakeInput;
    }

    _addListeners() {
        this._fakeInput.addEventListener('input', this._checkInputValue);
        this._fakeInput.addEventListener('focus', this._setFocusState);
        this._fakeInput.addEventListener('open:countrydropdown', this._setActiveState);
        this._fakeInput.addEventListener('close:countrydropdown', this._removeActiveState);
        this._fakeInput.addEventListener('blur', this._removeActiveState);
    }

    _setActiveState() {
        this._root.classList.add('active');
    }

    _setErrorState(validated = true) {
        if (validated) {
            this._root.classList.remove('invalid');
        } else {
            this._root.classList.add('invalid');
        }
    }

    _setFocusState() {
        this._input.dispatchEvent(new Event('focus'));
        this._setActiveState();
    }

    _removeActiveState() {
        this._root.classList.remove('active');
        this._checkInputValue();
    }

    _checkInputValueAfterInitialisation() {
        this._checkInputValueAndEmitEvent(true);
    }

    _checkInputValue() {
        this._checkInputValueAndEmitEvent(false);
    }

    _checkInputValueAndEmitEvent(isInitialising) {
        this._updateHiddenField();
        const validated = this.isValid();
        this._setErrorState(validated);
        this._emitInputPhoneValidation(validated, isInitialising);
    }

    _emitInputPhoneValidation(validated, isInitialising) {
        const customEvent = new CustomEvent('input-phone-validation', {detail: {validated, isInitialising}});
        this._input.dispatchEvent(customEvent);
    }

    _updateHiddenField() {
        // The idea is to update hidden field with the rightly formatted value, taken from plugin instance
        this._input.value = this._iti.getNumber();
        this._input.dispatchEvent(new Event('input'));
    }

    _removeListeners() {
        this._fakeInput.removeEventListener('input', this._checkInputValue);
        this._fakeInput.removeEventListener('focus', this._setFocusState);
        this._fakeInput.removeEventListener('open:countrydropdown', this._setActiveState);
        this._fakeInput.removeEventListener('close:countrydropdown', this._removeActiveState);
        this._fakeInput.removeEventListener('blur', this._removeActiveState);
    }
}
